const GWP_SETTINGS = window.gwpSettings || { enabled: false, tiersRaw: "", disclaimer: "" };
const GWP_DEBUG = false;

/* ===========================
   Parse tiers (variant id OR @product-handle)
   Example:
     50:@winter-care-bundle
     90:44887766554433
=========================== */
const GIFT_TIERS = (GWP_SETTINGS.tiersRaw || "")
  .split("\n")
  .map(l => l.trim())
  .filter(Boolean)
  .map(line => {
    const [thresholdStr, ...giftParts] = line.split(":");
    const threshold = Math.round(parseFloat(thresholdStr) * 100);

    if (isNaN(threshold)) return null;

    const gifts = giftParts
      .join(":")
      .split("|")
      .map(v => v.trim())
      .filter(Boolean)
      .map(v => {
        if (/^\d+$/.test(v)) {
          return { variantId: parseInt(v, 10) };
        }

        return {
          productHandle: v.startsWith("@") ? v.slice(1) : v
        };
      });

    return { threshold, gifts };
  })
  .filter(tier => tier && tier.gifts.length);

// Keep track of any resolved gift variant ids from handles
const RESOLVED_GWP_VARIANTS = new Set();

/* ===========================
   Helpers
=========================== */
async function resolveHandleToAvailableVariantId(handle) {
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;

    const product = await res.json();
    const firstAvailable = (product.variants || []).find(v => v.available);

    return firstAvailable ? firstAvailable.id : null;
  } catch {
    return null;
  }
}

async function resolveTierVariantId(tier) {
  if (tier.variantId) {
    try {
      const res = await fetch(`/variants/${tier.variantId}.js`);
      if (!res.ok) return null;

      const variant = await res.json();

      return variant && variant.available ? tier.variantId : null;
    } catch {
      return null;
    }
  }

  if (tier.productHandle) {
    return await resolveHandleToAvailableVariantId(tier.productHandle);
  }

  return null;
}

// Lightweight cross-tab lock, 5s TTL
const GWP_LOCK_KEY = 'gwp-lock';

function nowMs() {
  return Date.now();
}

function acquireGwpLock(ttlMs = 5000) {
  try {
    const raw = localStorage.getItem(GWP_LOCK_KEY);

    if (raw) {
      const { ts } = JSON.parse(raw);

      if (nowMs() - ts < ttlMs) {
        return false;
      }
    }

    localStorage.setItem(GWP_LOCK_KEY, JSON.stringify({ ts: nowMs() }));

    return true;
  } catch {
    return true;
  }
}

function releaseGwpLock() {
  try {
    localStorage.removeItem(GWP_LOCK_KEY);
  } catch {}
}

/* ===========================
   Collection trigger helpers
=========================== */
const GWP_COLLECTION_CACHE = new Map();

async function fetchCollectionProductIds(collectionHandle) {
  if (!collectionHandle) return new Set();

  if (GWP_COLLECTION_CACHE.has(collectionHandle)) {
    return GWP_COLLECTION_CACHE.get(collectionHandle);
  }

  try {
    const productIds = new Set();
    let page = 1;
    let keepGoing = true;

    while (keepGoing) {
      const res = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}`, {
        cache: 'no-store'
      });

      if (!res.ok) break;

      const data = await res.json();
      const products = data.products || [];

      products.forEach(function(product) {
        productIds.add(Number(product.id));
      });

      keepGoing = products.length === 250;
      page += 1;
    }

    GWP_COLLECTION_CACHE.set(collectionHandle, productIds);

    return productIds;
  } catch (e) {
    console.error('GWP collection fetch failed:', e);
    return new Set();
  }
}

async function isCollectionCountEligible(cart) {
  const rule = GWP_SETTINGS.collectionRule || {};
  const collectionHandle = String(rule.handle || '').trim();
  const minQty = parseInt(rule.minQty || 3, 10);

  if (!collectionHandle || !minQty) return false;

  const productIds = await fetchCollectionProductIds(collectionHandle);

  if (!productIds.size) return false;

  let eligibleQty = 0;

  cart.items.forEach(function(item) {
    const isGift =
      item.properties &&
      (item.properties._gwp === '1' || item.properties._gwp === 1);

    if (isGift) return;

    if (productIds.has(Number(item.product_id))) {
      eligibleQty += item.quantity || 0;
    }
  });

  return eligibleQty >= minQty;
}

/* ===========================
   Specific product trigger helpers

   Supported settings:
   window.gwpSettings.specificProductRule = {
     handle: 'product-handle'
   }

   or:

   window.gwpSettings.specificProductRule = {
     productId: 123456789
   }

   or:

   window.gwpSettings.specificProductRule = {
     variantId: 123456789
   }

   Optional:
   minQty: 1
=========================== */
const GWP_SPECIFIC_PRODUCT_CACHE = new Map();

async function resolveProductHandleToProductId(handle) {
  const cleanHandle = String(handle || '').trim();

  if (!cleanHandle) return null;

  if (GWP_SPECIFIC_PRODUCT_CACHE.has(cleanHandle)) {
    return GWP_SPECIFIC_PRODUCT_CACHE.get(cleanHandle);
  }

  try {
    const res = await fetch(`/products/${cleanHandle}.js`, {
      cache: 'no-store'
    });

    if (!res.ok) return null;

    const product = await res.json();
    const productId = product && product.id ? Number(product.id) : null;

    GWP_SPECIFIC_PRODUCT_CACHE.set(cleanHandle, productId);

    return productId;
  } catch (e) {
    console.error('GWP specific product fetch failed:', e);
    return null;
  }
}

async function isSpecificProductEligible(cart) {
  const rule = GWP_SETTINGS.specificProductRule || {};

  const handle = String(rule.handle || '').trim();
  const productIdFromSettings = Number(rule.productId || 0);
  const variantIdFromSettings = Number(rule.variantId || 0);
  const minQty = parseInt(rule.minQty || 1, 10);

  if (!handle && !productIdFromSettings && !variantIdFromSettings) {
    return false;
  }

  if (!minQty) return false;

  let resolvedProductId = productIdFromSettings;

  if (!resolvedProductId && handle) {
    resolvedProductId = await resolveProductHandleToProductId(handle);
  }

  let matchedQty = 0;

  cart.items.forEach(function(item) {
    const isGift =
      item.properties &&
      (item.properties._gwp === '1' || item.properties._gwp === 1);

    const isUnlockOffer =
      item.properties &&
      item.properties._unlock_offer;

    if (isGift || isUnlockOffer) return;

    if (variantIdFromSettings && Number(item.variant_id) === variantIdFromSettings) {
      matchedQty += item.quantity || 0;
      return;
    }

    if (resolvedProductId && Number(item.product_id) === resolvedProductId) {
      matchedQty += item.quantity || 0;
    }
  });

  return matchedQty >= minQty;
}

async function getGiftVariantIdsForThreshold(threshold) {
  const tiers = GIFT_TIERS.filter(t => t.threshold === threshold);

  if (!tiers.length) return [];

  const selectedVariantIds = [];

  for (const tier of tiers) {
    for (const gift of tier.gifts) {
      const variantId = await resolveTierVariantId(gift);

      if (variantId) {
        selectedVariantIds.push(Number(variantId));
        break;
      }
    }
  }

  return selectedVariantIds;
}

// Collection-count and specific-product triggers do not use a cart-value
// threshold. For those trigger types, use every gift in the first configured
// threshold group instead of only the first gift line.
async function getFirstGiftGroupFromSettings() {
  const checkedThresholds = new Set();

  for (const tier of GIFT_TIERS) {
    if (checkedThresholds.has(tier.threshold)) continue;

    checkedThresholds.add(tier.threshold);

    const variantIds = await getGiftVariantIdsForThreshold(tier.threshold);

    if (variantIds.length) return variantIds;
  }

  return [];
}

// Use all gifts from the highest eligible cart-value threshold. Gifts from
// lower thresholds are not cumulative.
function getCartSubtotalExcludingGifts(cart) {
  return (cart.items || []).reduce((subtotal, item) => {
    const isGift =
      item.properties &&
      (item.properties._gwp === "1" || item.properties._gwp === 1);

    if (isGift) return subtotal;

    let linePrice = item.final_line_price;

    if (typeof linePrice !== 'number') {
      linePrice = item.line_price;
    }

    if (typeof linePrice !== 'number') {
      linePrice = Number(item.final_price || item.price || 0) * Number(item.quantity || 0);
    }

    return subtotal + Number(linePrice || 0);
  }, 0);
}

async function getSubtotalGiftGroup(subtotal) {
  const eligibleThresholds = [...new Set(
    GIFT_TIERS
      .filter(tier => subtotal >= tier.threshold)
      .map(tier => tier.threshold)
  )].sort((a, b) => a - b);

  if (!eligibleThresholds.length) return [];

  const allGiftVariantIds = [];

  for (const threshold of eligibleThresholds) {
    const giftIds = await getGiftVariantIdsForThreshold(threshold);
    allGiftVariantIds.push(...giftIds);
  }

  return [...new Set(allGiftVariantIds)];
}

/* ===========================
   Core: runGWP
=========================== */
async function runGWP() {
  if (!GWP_SETTINGS.enabled || GIFT_TIERS.length === 0) return;

  if (!acquireGwpLock()) return;

  try {
    const cart = await fetch('/cart.js', {
      cache: 'no-store'
    }).then(r => r.json());

    let desiredGiftVariantIds = [];

    if (GWP_SETTINGS.triggerType === 'collection_count') {
      const eligible = await isCollectionCountEligible(cart);

      if (eligible) {
        desiredGiftVariantIds = await getFirstGiftGroupFromSettings();
      }

    } else if (GWP_SETTINGS.triggerType === 'specific_product') {
      const eligible = await isSpecificProductEligible(cart);

      if (eligible) {
        desiredGiftVariantIds = await getFirstGiftGroupFromSettings();
      }

    } else {
      // Gift line prices must never help the cart qualify for its own GWP.
      const qualifyingSubtotal = getCartSubtotalExcludingGifts(cart);
      desiredGiftVariantIds = await getSubtotalGiftGroup(qualifyingSubtotal);
    }

    const desiredGiftIds = new Set(desiredGiftVariantIds.map(Number));

    // 2. Find every current gift line in the cart.
    const currentGiftLines = [];

    cart.items.forEach((item, idx) => {
      const hasGwpProp =
        item.properties &&
        (item.properties._gwp === "1" || item.properties._gwp === 1);

      const isKnown = RESOLVED_GWP_VARIANTS.has(item.variant_id);

      if (hasGwpProp || isKnown) {
        currentGiftLines.push({
          line: idx + 1,
          variantId: Number(item.variant_id),
          quantity: item.quantity || 0
        });
      }
    });

    currentGiftLines.forEach(gift => {
      RESOLVED_GWP_VARIANTS.add(gift.variantId);
    });

    desiredGiftVariantIds.forEach(variantId => {
      RESOLVED_GWP_VARIANTS.add(Number(variantId));
    });

    // 3. Keep one of each desired gift, clamp it to quantity 1, and remove
    // gifts that do not belong to the winning threshold group. Process line
    // removals from the bottom up so cart line numbers stay valid.
    const keptGiftIds = new Set();
    const lineChanges = [];

    currentGiftLines.forEach(gift => {
      if (!desiredGiftIds.has(gift.variantId) || keptGiftIds.has(gift.variantId)) {
        lineChanges.push({ line: gift.line, quantity: 0 });
        return;
      }

      keptGiftIds.add(gift.variantId);

      if (gift.quantity !== 1) {
        lineChanges.push({ line: gift.line, quantity: 1 });
      }
    });

    lineChanges.sort((a, b) => b.line - a.line);

    let cartChanged = false;

    for (const change of lineChanges) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(change)
      });

      cartChanged = true;
    }

    // 4. Add every missing gift from the winning threshold in one request.
    const missingGiftIds = desiredGiftVariantIds.filter(
      variantId => !keptGiftIds.has(Number(variantId))
    );

    if (missingGiftIds.length) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: missingGiftIds.map(variantId => ({
            id: Number(variantId),
            quantity: 1,
            properties: {
              _gwp: "1"
            }
          }))
        })
      });

      cartChanged = true;
    }

    if (desiredGiftVariantIds.length) {
      sessionStorage.setItem('gwp-added', 'true');
    } else {
      sessionStorage.removeItem('gwp-added');
    }

    if (cartChanged) {
      refreshMiniCart();
    }

  } finally {
    releaseGwpLock();
  }
}
function normalizeGwpQuantityInput(container) {
  if (!container) return;

  container.querySelectorAll(
    'input.quantity__input[name="updates[]"], input.quantity__input[name^="updates["]'
  ).forEach((input) => {
    input.value = '1';
    input.defaultValue = '1';
    input.setAttribute('value', '1');
    input.setAttribute('min', '1');
    input.setAttribute('max', '1');
    input.setAttribute('step', '1');
    input.setAttribute('data-max-quantity', '1');
    input.readOnly = true;
    input.removeAttribute('required');
    input.setCustomValidity('');
  });
}
/* ===========================
   Hide controls for gift lines
=========================== */
function hideGiftControls() {
  const isGiftLineEl = (el) => {
    const text = el?.textContent || '';

    if (/_gwp\s*:?\s*1/i.test(text)) return true;

    const vidAttr = el?.getAttribute?.('data-variant-id');

    if (vidAttr && RESOLVED_GWP_VARIANTS.has(parseInt(vidAttr, 10))) {
      return true;
    }

    return false;
  };

  // Drawer / mini cart
  document.querySelectorAll('li[data-variant-id]').forEach((lineItem) => {
    if (!isGiftLineEl(lineItem)) return;

    lineItem.classList.add('is-gwp');
    normalizeGwpQuantityInput(lineItem);

    const qtyBox = lineItem.querySelector('.product-quantity');

    if (qtyBox) {
      qtyBox.style.display = 'none';
    }

    const priceWrapper = lineItem.querySelector(
      '.limoniapps-discountninja-cart-item-product-price, .cartprice, .price, dd.price'
    );

    if (priceWrapper) {
      priceWrapper.querySelectorAll(
        '.price, dd.price, .price__regular, .price__sale, .price-item, .price--end'
      ).forEach(el => el.remove());

      const discounts = priceWrapper.parentElement?.querySelector(
        'ul.discounts, .list-unstyled.discounts'
      );

      if (discounts) {
        discounts.style.display = 'none';
      }

      if (!priceWrapper.querySelector('.gwp-price-free')) {
        const badge = document.createElement('span');
        badge.textContent = 'GIFT';
        badge.classList.add('gwp-price-free');
        priceWrapper.appendChild(badge);
      }
    }

    let titleEl = lineItem.querySelector('.product-title');

    if (titleEl && titleEl.tagName === 'A') {
      const span = document.createElement('span');
      span.textContent = titleEl.textContent;
      span.className = titleEl.className;
      titleEl.replaceWith(span);
      titleEl = span;
    }

    lineItem.querySelectorAll('.gwp-gift-note, .gwp-online-only-note').forEach(n => n.remove());

    if (titleEl) {
      const note = document.createElement('span');
      note.className = 'gwp-gift-note';
      note.textContent = (GWP_SETTINGS.disclaimer || '').trim();

      if (note.textContent) {
        note.style.fontSize = '12px';
        note.style.marginTop = '4px';
        note.style.display = 'block';
        note.style.color = 'rgb(102, 102, 102)';
        titleEl.insertAdjacentElement('afterend', note);

        const onlineOnlyNote = document.createElement('span');
        onlineOnlyNote.className = 'gwp-online-only-note';
        onlineOnlyNote.textContent = 'Free gift redeemable online only';
        onlineOnlyNote.style.fontSize = '12px';
        onlineOnlyNote.style.marginTop = '2px';
        onlineOnlyNote.style.display = 'block';
        onlineOnlyNote.style.color = 'red';

        note.insertAdjacentElement('afterend', onlineOnlyNote);
      }
    }

    const imgLink = lineItem.querySelector('.product-image.media-wrapper');

    if (imgLink && imgLink.tagName === 'A') {
      const wrapper = document.createElement('div');
      wrapper.className = imgLink.className;
      wrapper.innerHTML = imgLink.innerHTML;
      imgLink.replaceWith(wrapper);
    }

    lineItem.querySelectorAll('.product-option').forEach(el => {
      el.style.display = 'none';
    });
  });

  // Cart page
  document.querySelectorAll('.template-cart [data-limoniapps-discountninja-cart-key]').forEach(el => {
    const row = el.closest('tr.cart-item');

    if (!row) return;

    const propsText = row.textContent || '';
    const key = el.dataset.limoniappsDiscountninjaCartKey || '';
    const variantId = parseInt(key.split(':')[0], 10);

    const isGift =
      /_gwp\s*:?\s*1/i.test(propsText) ||
      RESOLVED_GWP_VARIANTS.has(variantId);

    if (!isGift) return;

    row.classList.add('is-gwp');
normalizeGwpQuantityInput(row);

    const qtyEl = row.querySelector('.cart-item__quantity .quantity');

    if (qtyEl) {
      qtyEl.style.display = 'none';
    }

    const removeBtn = row.querySelector('cart-remove-button');

    if (removeBtn) {
      removeBtn.style.display = 'none';
    }

    const priceSpan = row.querySelector('.cart-item__totals .price.price--end');

    if (priceSpan) {
      priceSpan.style.display = 'none';
    }

    const priceBox = row.querySelector('.cart-item__price-wrapper');

    if (priceBox) {
      priceBox.textContent = 'FREE GIFT';
      priceBox.classList.add('gwp-price-free');
    }

    const titleLink = row.querySelector('a.cart-item__name');

    if (titleLink && titleLink.tagName === 'A') {
      const span = document.createElement('span');
      span.textContent = titleLink.textContent;
      span.className = titleLink.className;
      titleLink.replaceWith(span);
    }
  });
}

/* ===========================
   Unlock offer remove helper
=========================== */
async function removeUnlockOfferFromCart() {
  const settings = getUnlockSettings();
  const handle = settings.product_handle;

  if (!handle) return;

  try {
    const [cart, product] = await Promise.all([
      fetch('/cart.js', { cache: 'no-store' }).then(r => r.json()),
      fetchProductByHandle(handle)
    ]);

    if (!cart || !product || !product.variants || !product.variants.length) return;

    const variantIds = product.variants.map(function(v) {
      return Number(v.id);
    });

    const updates = {};
    let hasMatch = false;

    cart.items.forEach(function(item) {
      const isUnlockItem =
        variantIds.includes(Number(item.variant_id)) ||
        (item.properties && item.properties._unlock_offer);

      if (isUnlockItem) {
        updates[item.key] = 0;
        hasMatch = true;
      }
    });

    if (!hasMatch) return;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: updates
      })
    });

    refreshMiniCart();
  } catch (e) {
    console.error('Failed to remove unlock offer item:', e);
  }
}

/* ===========================
   Mini cart refresh
=========================== */
function refreshMiniCart() {
  const miniCart = document.querySelector('mini-cart');

  if (!miniCart || typeof miniCart.renderContents !== 'function') return;

  fetch('/?sections=mini-cart,cart-icon-bubble,mobile-cart-icon-bubble')
    .then((res) => res.json())
    .then((sections) => {
      miniCart.renderContents({
        id: Date.now().toString(),
        sections
      });

      setTimeout(() => {
        hideGiftControls();
        renderUnlockOffer();
      }, 300);
    });
}

/* =========================
   Unlock offer
========================= */
function getUnlockSettings() {
  return (window.gwpSettings && window.gwpSettings.unlockOffer) || {};
}

function parseThresholdToCents(value) {
  const num = parseFloat(String(value || '').replace(/[^0-9.]/g, ''));

  if (isNaN(num)) return 0;

  return Math.round(num * 100);
}

function formatUnlockMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

async function fetchProductByHandle(handle) {
  if (!handle) return null;

  try {
    const res = await fetch(`/products/${handle}.js`, {
      cache: 'no-store'
    });

    if (!res.ok) return null;

    return await res.json();
  } catch (e) {
    return null;
  }
}

function getUnlockOfferWrap() {
  return document.querySelector('[data-unlock-offer-wrap]');
}

function buildUnlockOfferHtml(product, variant, settings, alreadyInCart) {
  const image = product.featured_image ? product.featured_image : '';
  const title = settings.title || product.title;
  const wasPrice = settings.was_price || '';
  const discountPrice = settings.discount_price || '$5.00';
  const message = settings.message || `Youâ€™ve unlocked the ${title} for ${discountPrice}!`;
  const buttonText = alreadyInCart ? 'Added' : (settings.button_text || 'Add to Cart Now');

  return `
    <div class="unlock-offer-card" data-unlock-offer>
      <div class="unlock-offer-card__message">${message}</div>

      <div class="unlock-offer-card__box">
        <div class="unlock-offer-card__image">
          ${image ? `<img src="${image}" alt="${title}">` : ''}
        </div>

        <div class="unlock-offer-card__content">
          <h4 class="unlock-offer-card__title">${title}</h4>

          <div class="unlock-offer-card__price-wrap">
            ${wasPrice ? `<div class="unlock-offer-card__was">Was ${wasPrice}</div>` : ''}
            <div class="unlock-offer-card__price">${discountPrice}</div>
          </div>

          <button
            type="button"
            class="unlock-offer-card__btn"
            data-unlock-offer-add
            data-variant-id="${variant.id}"
            ${alreadyInCart ? 'disabled' : ''}
          >
            ${buttonText}
          </button>
        </div>
      </div>
    </div>
  `;
}

function getCartSubtotalExcludingUnlockItem(cart, product) {
  if (!cart || !cart.items) return 0;

  if (!product || !product.variants) {
    return cart.items_subtotal_price || 0;
  }

  const unlockVariantIds = product.variants.map(function(v) {
    return Number(v.id);
  });

  return cart.items.reduce(function(total, item) {
    const isUnlockItem =
      unlockVariantIds.includes(Number(item.variant_id)) ||
      (item.properties && item.properties._unlock_offer);

    if (isUnlockItem) return total;

    return total + (item.final_line_price || 0);
  }, 0);
}

async function syncUnlockOfferState() {
  const settings = getUnlockSettings();

  if (!settings.enabled || !settings.product_handle) return false;

  try {
    const [cart, product] = await Promise.all([
      fetch('/cart.js', { cache: 'no-store' }).then(function(r) {
        return r.json();
      }),
      fetchProductByHandle(settings.product_handle)
    ]);

    if (!cart || !product || !product.variants || !product.variants.length) {
      return false;
    }

    const unlockVariantIds = product.variants.map(function(v) {
      return Number(v.id);
    });

    const subtotalExcludingUnlock = cart.items.reduce(function(total, item) {
      const isUnlockItem =
        unlockVariantIds.includes(Number(item.variant_id)) ||
        (item.properties && item.properties._unlock_offer);

      if (isUnlockItem) return total;

      return total + (item.final_line_price || 0);
    }, 0);

    const thresholdCents = parseThresholdToCents(settings.threshold);

    const updates = {};
    let shouldRemove = false;

    cart.items.forEach(function(item) {
      const isUnlockItem =
        unlockVariantIds.includes(Number(item.variant_id)) ||
        (item.properties && item.properties._unlock_offer);

      if (isUnlockItem && subtotalExcludingUnlock < thresholdCents) {
        updates[item.key] = 0;
        shouldRemove = true;
      }
    });

    if (!shouldRemove) return false;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: updates
      })
    });

    refreshMiniCart();

    return true;
  } catch (e) {
    console.error('syncUnlockOfferState failed:', e);
    return false;
  }
}

async function renderUnlockOffer() {
  const settings = getUnlockSettings();
  const wrap = getUnlockOfferWrap();

  if (!wrap) return;

  if (!settings.enabled) {
    wrap.innerHTML = '';
    return;
  }

  const thresholdCents = parseThresholdToCents(settings.threshold);
  const handle = settings.product_handle;

  if (!thresholdCents || !handle) {
    wrap.innerHTML = '';
    return;
  }

  try {
    const [cart, product] = await Promise.all([
      fetch('/cart.js', { cache: 'no-store' }).then(r => r.json()),
      fetchProductByHandle(handle)
    ]);

    if (!cart || !product || !product.variants || !product.variants.length) {
      wrap.innerHTML = '';
      return;
    }

    const subtotal = getCartSubtotalExcludingUnlockItem(cart, product);

    if (subtotal < thresholdCents) {
      wrap.innerHTML = '';

      const unlockVariantIds = product.variants.map(function(v) {
        return Number(v.id);
      });

      const hasUnlockItem = cart.items.some(function(item) {
        return (
          unlockVariantIds.includes(Number(item.variant_id)) ||
          (item.properties && item.properties._unlock_offer)
        );
      });

      if (hasUnlockItem) {
        removeUnlockOfferFromCart();
      }

      return;
    }

    const variant = product.variants.find(v => v.available) || product.variants[0];

    if (!variant) {
      wrap.innerHTML = '';
      return;
    }

    const alreadyInCart = cart.items.some(item => Number(item.variant_id) === Number(variant.id));

    wrap.innerHTML = buildUnlockOfferHtml(product, variant, settings, alreadyInCart);
  } catch (e) {
    wrap.innerHTML = '';
  }
}

async function addUnlockOfferToCart(variantId, button) {
  if (!variantId) return;

  if (button) {
    button.disabled = true;
    button.textContent = 'Adding...';
  }

  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _unlock_offer: 'true'
            }
          }
        ]
      })
    });

    refreshMiniCart();

    setTimeout(function() {
      renderUnlockOffer();
    }, 400);
  } catch (e) {
    if (button) {
      const settings = getUnlockSettings();

      button.disabled = false;
      button.textContent = settings.button_text || 'Add to Cart Now';
    }
  }
}

/* ===========================
   Init
=========================== */

// Prevent the hidden GWP quantity input from blocking checkout
document.addEventListener('click', function(e) {
  const checkoutControl = e.target.closest(
    '[name="checkout"], button[type="submit"][data-checkout], a[href="/checkout"]'
  );

  if (!checkoutControl) return;

  hideGiftControls();
}, true);


document.addEventListener('click', function(e) {
  const btn = e.target.closest('[data-unlock-offer-add]');

  if (!btn) return;

  e.preventDefault();

  addUnlockOfferToCart(btn.getAttribute('data-variant-id'), btn);
});

document.addEventListener('DOMContentLoaded', () => {
  runGWP();
  renderUnlockOffer();

  setTimeout(() => {
    hideGiftControls();
  }, 300);

  document.addEventListener('cartdrawer:opened', () => {
    setTimeout(() => {
      runGWP();
      renderUnlockOffer();
      hideGiftControls();
    }, 500);
  });

  document.addEventListener('cart:updated', () => {
    if (window.location.pathname === '/cart') {
      setTimeout(async () => {
        const removed = await syncUnlockOfferState();

        if (removed) return;

        const cart = await fetch('/cart.js').then(function(r) {
          return r.json();
        });

        if (cart.items && cart.items.length > 0) {
          runGWP();
          renderUnlockOffer();
          hideGiftControls();
        } else {
          sessionStorage.removeItem('gwp-added');
          renderUnlockOffer();
        }
      }, 120);
    } else {
      requestAnimationFrame(() => {
        setTimeout(async () => {
          const removed = await syncUnlockOfferState();

          if (removed) return;

          runGWP();
          renderUnlockOffer();
          hideGiftControls();
        }, 60);
      });
    }
  });
});